import { defineStore } from "pinia";
import {
  GET_WISHES,
  WISHES_DISINHERIT,
  WISHES_DEPENDENTS_TRUST,
  WISHES_PET_TRUST,
  CUSTOMER_OBJECT,
  CASH_BEQUEST,
  TOTAL_CASH_BEQUEST,
  TEMPLATES,
  WISHES_EOL_ROLE,
  ASSET_BEQUEST_RECIPIENT,
} from "@/api/apiRoute";
import { handleApiResponse, handleError } from "@/utils/common.js";
import { authData } from "@/api/apiService.js";
import { errorMessage } from "@/utils/errorMessages";
import { useCustomerStore } from "@/stores/customerObjectStore";

const ASSET_BEQUESTS = "/wishes/bequests/assets";

export const useWishesStore = defineStore("wishesStore", {
  state: () => ({
    loading: false,
    wishes: null,
    error: null,
    totalBequestAmount: 0,
    cashBequests: [],
    assetBequests: [],
    remainders: [],
    errorMsg: "",
    errorDisplayDuration: 4000,
    petList: [],
    isLoadingTotal: false,
    wishesData: null,
    beneficiaryList: [],
    assetBequestsData: [],
    isLoadingAssetBequests: false,
    isUpdatingRemainders: false,
    deleteTotalRemainders: false,
    _isInitializing: false,
    _hasInitialized: false,
    // Add unsaved changes tracking
    hasUnsavedChanges: false,
    unsavedChangesData: null,
    showUnsavedChangesModal: false,
    pendingNavigation: null,
    isPageReload: false,
  }),

  // Add this getter to handle individual bequest amount conversion
  // Add this to the getters section of your store
  getters: {
    getWishesData: (state) => state.wishes,
    isLoading: (state) => state.loading,
    getTotalBequestAmount: (state) => state.totalBequestAmount,

    getAssetBequestsData: (state) => state.assetBequestsData,

    getAssetRecipientByRole: (state) => (assetId, role) => {
      return state.assetBequestsData.find(
        (bequest) => bequest.assetObjectId === assetId && bequest.role === role
      );
    },

    getAssetRecipients: (state) => (assetId) => {
      return state.assetBequestsData.filter(
        (bequest) => bequest.assetObjectId === assetId
      );
    },

    // Add this new getter to convert individual bequest amounts from cents to dollars
    getCashBequestInDollars: (state) => (beneficiaryId) => {
      const cashBequest = state.cashBequests.find(
        (cb) => cb.bequeathedToObjectId === beneficiaryId
      );
      // Convert cents to dollars for display
      return cashBequest ? cashBequest.bequestAmount / 100 : 0;
    },
  },

  actions: {
    // Set unsaved changes flag
    setUnsavedChanges() {
      this.hasUnsavedChanges = true;
    },

    // Clear unsaved changes flag
    clearUnsavedChanges() {
      this.hasUnsavedChanges = false;
      this.isPageReload = false;
    },

    // Modal actions for unsaved changes
    openModal() {
      this.showUnsavedChangesModal = true;
    },

    closeModal() {
      this.showUnsavedChangesModal = false;
      this.pendingNavigation = null;
    },

    confirmNavigation() {
      this.clearUnsavedChanges();
      this.closeModal();
      if (this.pendingNavigation) {
        // Execute the stored navigation
        this.pendingNavigation();
        this.pendingNavigation = null;
      }
    },

    cancelNavigation() {
      this.closeModal();
    },

    async initialize() {
      if (this._isInitializing || this._hasInitialized) {
        return;
      }

      this._isInitializing = true;
      this.loading = true;

      try {
        const [wishesResponse, assetBequestsResponse] = await Promise.all([
          authData.get(GET_WISHES),
          authData.get(ASSET_BEQUESTS),
        ]);

        if (wishesResponse.data?.data) {
          this.wishes = wishesResponse.data.data;
          this.cashBequests = wishesResponse.data.data?.cashBequests || [];
          this.assetBequests = wishesResponse.data.data?.assetBequests || [];
          this.remainders = wishesResponse.data.data?.remainders || [];
        }

        if (assetBequestsResponse.data?.data) {
          this.assetBequestsData = assetBequestsResponse.data.data;
        }

        await this.updateTotalBequestAmount();

        this._hasInitialized = true;
        return { success: true };
      } catch (error) {
        console.error("Error initializing wishes store:", error);
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
        this._isInitializing = false;
      }
    },

    async fetchRemainders() {
      try {
        const response = await authData.get(`/wishes/remainders`);
        this.remainders = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        console.error("Error fetching remainders:", error);
        this.showError(error);
        throw error;
      }
    },

    async deleteAllAssignedRemainders() {
      try {
        this.deleteTotalRemainders = true;
        const response = await authData.delete(`/wishes/remainders`);
        this.fetchRemainders();
        return handleApiResponse(response);
      } catch (error) {
        console.error("Error deleting all assigned remainders:", error);
        this.showError(error);
        throw error;
      } finally {
        this.deleteTotalRemainders = false;
      }
    },

    async updateRemainder(objectId, percentage) {
      try {
        this.isUpdatingRemainders = true;
        const response = await authData.put(`/wishes/remainders/${objectId}`, {
          remainderPercent: percentage,
        });
        this.fetchRemainders();
        return handleApiResponse(response);
      } catch (error) {
        console.error("Error updating remainder:", error);
        this.showError(error);
        throw error;
      } finally {
        this.isUpdatingRemainders = false;
      }
    },

    async deleteRemainder(objectId) {
      try {
        const response = await authData.delete(
          `/wishes/remainders/${objectId}`
        );
        return handleApiResponse(response);
      } catch (error) {
        console.error("Error deleting remainder:", error);
        this.showError(error);
        throw error;
      }
    },

    clearErrorMessage() {
      this.errorMsg = "";
    },

    setErrorMessage(message) {
      this.errorMsg = errorMessage[message] || message || "An error occurred";
    },

    showError(error) {
      this.setErrorMessage(error?.response?.data?.message);
      setTimeout(() => {
        this.clearErrorMessage();
      }, this.errorDisplayDuration);
      handleError(error);
      throw error;
    },

    async fetchAssetBequests(force = false) {
      if (!force && this.isLoadingAssetBequests) {
        return;
      }

      this.isLoadingAssetBequests = true;
      try {
        const response = await authData.get(ASSET_BEQUESTS);
        if (response.data?.data) {
          this.assetBequestsData = response.data.data;
        }
        return handleApiResponse(response);
      } catch (error) {
        console.error("Error fetching asset bequests:", error);
        this.showError(error);
        throw error;
      } finally {
        this.isLoadingAssetBequests = false;
      }
    },

    async fetchWishes() {
      this.loading = true;
      this.error = null;
      try {
        const response = await authData.get(GET_WISHES);
        if (response.data?.data) {
          this.wishes = response?.data?.data;
          this.cashBequests = response?.data?.data?.cashBequests || [];
          this.assetBequests = response?.data?.data?.assetBequests || [];
          this.remainders = response?.data?.data?.remainders || [];
          await this.updateTotalBequestAmount();
        }

        return handleApiResponse(response);
      } catch (err) {
        this.error = err.response?.data?.message || "Failed to fetch wishes";
        console.error("Error fetching wishes:", err);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async getDisinheritData() {
      this.loading = true;
      try {
        const response = await authData.get(WISHES_DISINHERIT);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async disinheritIndividual(id) {
      this.loading = true;
      try {
        const response = await authData.put(`${WISHES_DISINHERIT}/${id}`);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async removeDisinheritIndividual(id) {
      this.loading = true;
      try {
        const response = await authData.delete(`${WISHES_DISINHERIT}/${id}`);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getDependentsTrustData() {
      this.loading = true;
      try {
        const response = await authData.get(WISHES_DEPENDENTS_TRUST);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async addDependentsTrustAgeBreakdowns(id, payload) {
      this.loading = true;
      try {
        const response = await authData.put(
          `${WISHES_DEPENDENTS_TRUST}/${id}`,
          payload
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteDependentsTrustAgeBreakdowns(id, age) {
      this.loading = true;
      try {
        const response = await authData.delete(
          `${WISHES_DEPENDENTS_TRUST}/${id}/age/${age}`
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getPets() {
      this.loading = true;
      try {
        const response = await authData.get(
          `${CUSTOMER_OBJECT}?includeFields=yes&template=pet`
        );
        this.petList = response.data.data || [];
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getPetTrustData() {
      this.loading = true;
      try {
        const response = await authData.get(WISHES_PET_TRUST);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async createPetTrust(payload) {
      this.loading = true;
      try {
        const response = await authData.post(WISHES_PET_TRUST, payload);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getSinglePetTrust(trustId) {
      this.loading = true;
      try {
        const response = await authData.get(`${WISHES_PET_TRUST}/${trustId}`);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updatePetTrust(id, payload) {
      this.loading = true;
      try {
        const response = await authData.patch(
          `${WISHES_PET_TRUST}/${id}`,
          payload
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async savePetToTrust(trustId, petId, payload) {
      this.loading = true;
      try {
        const response = await authData.put(
          `${WISHES_PET_TRUST}/${trustId}/pets/${petId}`,
          payload
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async removePetToTrust(trustId, petId) {
      this.loading = true;
      try {
        const response = await authData.delete(
          `${WISHES_PET_TRUST}/${trustId}/pets/${petId}`
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deletePetTrust(trustId) {
      this.loading = true;
      try {
        const response = await authData.delete(
          `${WISHES_PET_TRUST}/${trustId}`
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getWishes() {
      this.loading = true;
      try {
        const response = await authData.get(GET_WISHES);
        this.wishesData = response.data.data;
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async saveWishes(data) {
      this.loading = true;
      try {
        const response = await authData.patch(GET_WISHES, data);
        this.getWishes();
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async submitCustomerObject(payload) {
      this.loading = true;
      try {
        const response = await authData.post(`${CUSTOMER_OBJECT}`, payload);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateCustomerObjectField(cObId, fieldId, payload) {
      this.loading = true;
      try {
        const response = await authData.patch(
          `${CUSTOMER_OBJECT}/${cObId}/fields/${fieldId}`,
          payload
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getCustomerObject(id) {
      this.loading = true;
      try {
        const response = await authData.get(`${CUSTOMER_OBJECT}/${id}`);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getTemplate(templateIdOrSlug) {
      this.loading = true;
      try {
        const response = await authData.get(`${TEMPLATES}/${templateIdOrSlug}`);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async getBeneficiaries(query) {
      this.loading = true;
      if (this.beneficiaryList.length) {
        this.loading = false;
        return this.beneficiaryList;
      }
      try {
        const response = await authData.get(`${CUSTOMER_OBJECT}${query}`);
        this.beneficiaryList = response.data.data || [];
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async addEOLRole(id, data) {
      this.loading = true;
      try {
        const response = await authData.put(`${WISHES_EOL_ROLE}/${id}`, data);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async removeEOLRole(id) {
      this.loading = true;
      try {
        const response = await authData.delete(`${WISHES_EOL_ROLE}/${id}`);
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateTotalBequestAmount() {
      this.isLoadingTotal = true;
      try {
        const response = await authData.get(`${TOTAL_CASH_BEQUEST}/total`);
        if (response.data?.data?.total !== undefined) {
          // Convert cents to dollars for display
          this.totalBequestAmount = response.data.data.total / 100;
        }
        return response.data?.data?.total || 0;
      } catch (err) {
        console.error("Error fetching total bequest amount:", err);
        // Fallback calculation - also convert from cents to dollars
        this.totalBequestAmount = this.cashBequests.reduce((total, bequest) => {
          return total + (bequest.bequestAmount || 0) / 100;
        }, 0);
        return this.totalBequestAmount;
      } finally {
        this.isLoadingTotal = false;
      }
    },

    getAssetRecipient(assetId, role) {
      return this.assetBequestsData.find(
        (bequest) => bequest.assetObjectId === assetId && bequest.role === role
      );
    },

    async assignCashBequest(objectId, payload) {
      this.loading = true;
      this.error = null;
      try {
        const response = await authData.put(CASH_BEQUEST(objectId), payload);
        if (response.data?.data) {
          const updatedCashBequest = response.data.data;

          const existingIndex = this.cashBequests.findIndex(
            (item) =>
              item.bequeathedToObjectId === objectId || item.id === objectId
          );

          if (existingIndex !== -1) {
            this.cashBequests[existingIndex] = {
              ...this.cashBequests[existingIndex],
              ...updatedCashBequest,
              bequeathedToObjectId: objectId,
              // Store the amount as received from API (in cents) - conversion happens in getters/display
              bequestAmount: updatedCashBequest.bequestAmount,
            };
          } else {
            this.cashBequests.push({
              ...updatedCashBequest,
              bequeathedToObjectId: objectId,
              // Store the amount as received from API (in cents)
              bequestAmount: updatedCashBequest.bequestAmount,
            });
          }

          await this.updateTotalBequestAmount();

          if (this.wishes) {
            this.wishes = {
              ...this.wishes,
              cashBequests: [...this.cashBequests],
            };
          }
        }
        return handleApiResponse(response);
      } catch (err) {
        this.error =
          err.response?.data?.message || "Failed to assign cash bequest";
        console.error("Error assigning cash bequest:", err);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async deleteCashBequest(objectId) {
      this.loading = true;
      this.error = null;
      try {
        const response = await authData.delete(CASH_BEQUEST(objectId));

        const index = this.cashBequests.findIndex(
          (item) =>
            item.bequeathedToObjectId === objectId || item.id === objectId
        );

        if (index !== -1) {
          this.cashBequests.splice(index, 1);
          await this.updateTotalBequestAmount();

          if (this.wishes) {
            this.wishes = {
              ...this.wishes,
              cashBequests: [...this.cashBequests],
            };
          }
        }

        return handleApiResponse(response);
      } catch (err) {
        this.error =
          err.response?.data?.message || "Failed to delete cash bequest";
        console.error("Error deleting cash bequest:", err);
        throw err;
      } finally {
        this.loading = false;
      }
    },

    async getTotalCashBequest() {
      this.loading = true;
      this.error = null;
      try {
        const response = await authData.get(`${TOTAL_CASH_BEQUEST}/total`);
        if (response.data?.data?.total !== undefined) {
          // Convert cents to dollars for display
          this.totalBequestAmount = response.data.data.total / 100;
        }
        return handleApiResponse(response);
      } catch (err) {
        this.error =
          err.response?.data?.message || "Failed to get total cash bequest";
        console.error("Error getting total cash bequest:", err);
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async refreshTotal() {
      try {
        await this.updateTotalBequestAmount();
      } catch (err) {
        console.error("Error refreshing total:", err);
      }
    },

    // CHANGE 5: Update assignAssetRecipient function (around line 391)
    async assignAssetRecipient(assetId, toObjectId, role) {
      try {
        this.loading = true;

        const payload = { role: role };

        const response = await authData.put(
          ASSET_BEQUEST_RECIPIENT(assetId, toObjectId),
          payload
        );

        if (response?.data) {
          await this.fetchAssetBequests(true);

          const customerStore = useCustomerStore();
          await customerStore.getCustomerObject({
            category: "estate-assets",
          });

          return { success: true, data: response.data };
        }
        return { success: false, error: "Failed to assign recipient" };
      } catch (error) {
        this.showError(error);
        return {
          success: false,
          error: error.message || "Failed to assign recipient",
        };
      } finally {
        this.loading = false;
      }
    },

    // CHANGE 6: Update removeAssetRecipient function (around line 428)
    async removeAssetRecipient(assetId, toObjectId) {
      try {
        this.loading = true;
        const response = await authData.delete(
          ASSET_BEQUEST_RECIPIENT(assetId, toObjectId)
        );

        // Force refresh after removal
        await this.fetchAssetBequests(true);

        return { success: true, data: response.data };
      } catch (error) {
        console.error("Error removing asset recipient:", error);
        return {
          success: false,
          error: error.response?.data?.message || "Error removing recipient",
        };
      } finally {
        this.loading = false;
      }
    },

    async updateCustomerObject(id, payload) {
      this.loading = true;
      try {
        const response = await authData.patch(
          `${CUSTOMER_OBJECT}/${id}`,
          payload
        );
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchAllBequestCash() {
      this.loading = true;
      try {
        const response = await authData.get(TOTAL_CASH_BEQUEST);
        if (response.data?.data) {
          this.cashBequests = response.data.data;
        }
        return handleApiResponse(response);
      } catch (error) {
        this.showError(error);
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
});
